import PropTypes from "prop-types";
import { Card, Checkbox, Input, List, Loader, Pagination } from "semantic-ui-react";

import { classNames } from "../../../libs/component_utils";

import "./millerPicker.css";
import { useMemo } from "react";

export default function MillerPicker({ disabled, children, ...props }) {
  return (
    <div
      className={classNames("miller-picker", disabled && "disabled")}
      {...props}
    >
      {children}
    </div>
  );
}

const MillerPickerColumn = ({
  activeItem,
  activePage,
  allItems,
  indeterminate = new Set(),
  items = [],
  loading,
  onChange,
  onSearchChange,
  onSelect,
  search = false,
  searchPlaceholder = "Search",
  searchQuery = "",
  selection,
  title,
  totalPages,
  onPageChange,
  value: columnValue,
  ...props
}) => {
  const effectiveAllItems = allItems ?? items; // fallback to items for non-paginated columns
  const selectionSet = useMemo(() => new Set(columnValue), [columnValue]);
  const { allSelected, someSelected } = useMemo(() => {
    if (!effectiveAllItems || effectiveAllItems.length === 0) return { allSelected: false, someSelected: false };
    let all = true;
    let some = false;
    for (const { value } of effectiveAllItems) {
      if (selectionSet.has(value)) {
        some = true;
      } else {
        all = false;
      }
      if (!all && some) break; // short circuit if we already know the result
    }
    return { allSelected: all, someSelected: some };
  }, [effectiveAllItems, selectionSet]);
  return (
    <Card
      className="miller-picker-column"
      {...props}
    >
      <Card.Content className="header-content">
        <Card.Header>
          <Checkbox
            checked={effectiveAllItems.length > 0 && allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e, { checked }) => {
              e.stopPropagation();
              onChange(checked ? effectiveAllItems.map(({ value }) => value) : []);
            }}
          />
          {title}
        </Card.Header>
      </Card.Content>
      <Card.Content
        className="miller-picker-list"
        extra
      >
        {loading ? (
          <Loader active />
        ) : (
          <List selection={selection}>
            {items.map(({ value, text }) => (
              <List.Item
                active={value === activeItem}
                key={value}
                onClick={onSelect ? () => onSelect(value) : null}
              >
                <Checkbox
                  checked={selectionSet.has(value)}
                  indeterminate={indeterminate.has(value) && !selectionSet.has(value)}
                  onChange={(e, { checked }) => {
                    e.stopPropagation();
                    onChange(checked ? [...columnValue, value] : columnValue.filter((v) => value !== v));
                  }}
                />
                {text}
              </List.Item>
            ))}
          </List>
        )}
      </Card.Content>
      {totalPages > 1 && (
        <Card.Content
          className="miller-picker-pagination"
          extra
        >
          <Pagination
            activePage={activePage}
            ellipsisItem={null}
            siblingRange={1}
            totalPages={totalPages}
            onPageChange={onPageChange}
            size="mini"
            firstItem={null}
            lastItem={null}
          />
        </Card.Content>
      )}
      {search ? (
        <Card.Content
          className="miller-picker-search"
          extra
        >
          <Input
            autoComplete="off"
            fluid
            icon={
              searchQuery !== ""
                ? {
                    name: "x",
                    className: "input-clear-icon",
                    link: true,
                    onClick: () => onSearchChange(""),
                  }
                : "search"
            }
            iconPosition={searchQuery !== "" ? null : "left"}
            onChange={(_, { value }) => onSearchChange(value)}
            placeholder={searchPlaceholder}
            transparent
            value={searchQuery}
          />
        </Card.Content>
      ) : null}
    </Card>
  );
};

MillerPickerColumn.propTypes = {
  activeItem: PropTypes.any,
  activePage: PropTypes.number,
  allItems: PropTypes.arrayOf(PropTypes.object),
  indeterminate: PropTypes.instanceOf(Set),
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  onSelect: PropTypes.func,
  search: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchQuery: PropTypes.string,
  selection: PropTypes.bool,
  title: PropTypes.string.isRequired,
  totalPages: PropTypes.number,
  value: PropTypes.array.isRequired,
};

MillerPicker.Column = MillerPickerColumn;
